import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  UserPlus,
  PencilSimple,
  Key,
  Prohibit,
  CircleNotch,
  ShieldCheck,
  User,
  Briefcase,
  Wrench,
  ChartBar,
  SlidersHorizontal,
  Check,
  X,
  HardHat
} from '@phosphor-icons/react';


const ROLE_CONFIG = {
  admin: { label: 'Administrador', icon: ShieldCheck, color: 'bg-red-100 text-red-700' },
  manager: { label: 'Gerente', icon: ChartBar, color: 'bg-purple-100 text-purple-700' },
  sales: { label: 'Vendedor', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  engineer: { label: 'Ingeniero', icon: HardHat, color: 'bg-teal-100 text-teal-700' },
  technician: { label: 'Tecnico', icon: Wrench, color: 'bg-amber-100 text-amber-700' },
  client: { label: 'Cliente', icon: User, color: 'bg-green-100 text-green-700' },
};

// Granular permissions: View / Create / Edit / Delete per module
const PERMISSION_MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    actions: ['view'],
  },
  {
    key: 'leads',
    label: 'Leads',
    actions: ['view', 'create', 'edit', 'delete'],
  },
  {
    key: 'clients',
    label: 'Clientes',
    actions: ['view', 'create', 'edit', 'delete'],
  },
  {
    key: 'quotes',
    label: 'Cotizaciones',
    actions: ['view', 'create', 'edit', 'approve', 'send'],
  },
  {
    key: 'work_orders',
    label: 'Ordenes de Trabajo',
    actions: ['view', 'create', 'edit', 'complete', 'delete'],
  },
  {
    key: 'projects',
    label: 'Proyectos',
    actions: ['view', 'create', 'edit', 'delete'],
  },
  {
    key: 'inventory',
    label: 'Inventario',
    actions: ['view', 'manage'],
  },
  {
    key: 'technicians',
    label: 'Tecnicos',
    actions: ['view', 'manage'],
  },
  {
    key: 'calendar',
    label: 'Calendario',
    actions: ['view', 'manage'],
  },
  {
    key: 'payments',
    label: 'Pagos',
    actions: ['view', 'manage'],
  },
  {
    key: 'documents',
    label: 'Documentos',
    actions: ['view', 'manage'],
  },
  {
    key: 'reports',
    label: 'Reportes',
    actions: ['view'],
  },
  {
    key: 'b2b',
    label: 'B2B / Prospeccion',
    actions: ['view', 'manage'],
  },
  {
    key: 'catalog',
    label: 'Catalogo Precios',
    actions: ['view', 'edit'],
  },
  {
    key: 'finances',
    label: 'Finanzas',
    actions: ['view', 'edit'],
  },
  {
    key: 'hrms',
    label: 'Recursos Humanos',
    actions: ['view', 'manage'],
  },
  {
    key: 'construction',
    label: 'Construccion',
    actions: ['view', 'manage'],
  },
  {
    key: 'users',
    label: 'Usuarios',
    actions: ['manage'],
  },
  {
    key: 'settings',
    label: 'Configuracion',
    actions: ['manage'],
  },
];

const ACTION_LABELS = {
  view: 'Ver',
  create: 'Crear',
  edit: 'Editar',
  delete: 'Eliminar',
  approve: 'Aprobar',
  send: 'Enviar',
  complete: 'Completar',
  manage: 'Gestionar',
};

// For sidebar nav compatibility (maps module keys to nav paths)
const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'leads', label: 'Leads' },
  { key: 'clients', label: 'Clientes' },
  { key: 'quotes', label: 'Cotizaciones' },
  { key: 'invoices', label: 'Facturas' },
  { key: 'work_orders', label: 'Ordenes de Trabajo' },
  { key: 'calendar', label: 'Calendario' },
  { key: 'inventory', label: 'Inventario' },
  { key: 'technicians', label: 'Tecnicos' },
  { key: 'catalog', label: 'Catalogo' },
  { key: 'payments', label: 'Pagos' },
  { key: 'payment_links', label: 'Links de Pago' },
  { key: 'payroll', label: 'Payroll' },
  { key: 'documents', label: 'Documentos' },
  { key: 'reports', label: 'Reportes' },
  { key: 'users', label: 'Usuarios' },
  { key: 'prospects', label: 'Prospectos B2B' },
  { key: 'manual', label: 'Manual' },
  { key: 'projects', label: 'Proyectos' },
];

// Default modules per role (when no custom permissions set)
const ROLE_DEFAULTS = {
  admin: ALL_MODULES.map(m => m.key),
  manager: ['dashboard', 'pipeline', 'leads', 'clients', 'quotes', 'invoices', 'work_orders', 'calendar', 'inventory', 'technicians', 'catalog', 'payments', 'payment_links', 'payroll', 'documents', 'reports', 'users', 'prospects', 'manual', 'projects'],
  sales: ['dashboard', 'pipeline', 'leads', 'clients', 'quotes', 'invoices', 'catalog', 'calendar', 'payments', 'payment_links', 'reports', 'prospects', 'manual', 'projects'],
  engineer: ['dashboard', 'work_orders', 'calendar', 'inventory', 'projects', 'documents', 'technicians', 'reports', 'clients', 'manual'],
  technician: ['dashboard', 'work_orders', 'calendar', 'inventory', 'manual'],
  client: ['dashboard'],
};

function RoleBadge({ role }) {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.sales;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showPermissions, setShowPermissions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'sales', phone: '' });
  const [resetPw, setResetPw] = useState('');
  const [permissionsForm, setPermissionsForm] = useState([]);
  const [useCustomPerms, setUseCustomPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // Default granular permissions per role (maps to backend AVAILABLE_PERMISSIONS keys)
  const DEFAULT_ROLE_PERMISSIONS_MAP = {
    admin: Object.keys(ACTION_LABELS).flatMap(action =>
      PERMISSION_MODULES.filter(m => m.actions.includes(action)).map(m => `${m.key}_${action}`)
    ),
    manager: [
      'dashboard_view', 'leads_view', 'leads_create', 'leads_edit',
      'clients_view', 'clients_create', 'clients_edit',
      'quotes_view', 'quotes_create', 'quotes_edit', 'quotes_approve', 'quotes_send',
      'work_orders_view', 'work_orders_create', 'work_orders_edit',
      'inventory_view', 'technicians_view', 'calendar_view', 'calendar_manage',
      'reports_view', 'payments_view', 'projects_view', 'projects_create', 'projects_edit',
      'documents_view', 'documents_manage'
    ],
    sales: [
      'dashboard_view', 'leads_view', 'leads_create', 'leads_edit',
      'clients_view', 'clients_create', 'clients_edit',
      'quotes_view', 'quotes_create', 'quotes_edit',
      'calendar_view', 'calendar_manage'
    ],
    engineer: [
      'dashboard_view', 'clients_view',
      'work_orders_view', 'work_orders_edit', 'work_orders_complete',
      'projects_view', 'projects_edit',
      'inventory_view', 'calendar_view', 'calendar_manage',
      'documents_view', 'documents_manage',
      'technicians_view', 'reports_view'
    ],
    technician: [
      'dashboard_view', 'clients_view',
      'work_orders_view', 'work_orders_complete',
      'inventory_view', 'calendar_view'
    ]
  };

  const [granularPerms, setGranularPerms] = useState([]);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user-management`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/user-management/${editingUser.id}`, {
          name: form.name,
          role: form.role,
          phone: form.phone
        }, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/user-management`, form, { withCredentials: true });
      }
      setShowForm(false);
      setEditingUser(null);
      setForm({ email: '', password: '', name: '', role: 'sales', phone: '' });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setForm({ email: user.email, password: '', name: user.name, role: user.role, phone: user.phone || '' });
    setShowForm(true);
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Desactivar este usuario?')) return;
    try {
      await axios.delete(`${API_URL}/api/user-management/${userId}`, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPw || resetPw.length < 6) {
      alert('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/user-management/${userId}/reset-password`, { password: resetPw }, { withCredentials: true });
      alert('Contrasena actualizada');
      setShowResetPassword(null);
      setResetPw('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error');
    }
  };

  const openPermissions = (u) => {
    const hasCustom = u.permissions && u.permissions.length > 0;
    setUseCustomPerms(hasCustom);
    setPermissionsForm(hasCustom ? [...u.permissions] : [...(ROLE_DEFAULTS[u.role] || [])]);
    // Initialize granular perms from user's granular_permissions or role defaults
    const userGranular = u.granular_permissions || [];
    if (userGranular.length > 0) {
      setGranularPerms([...userGranular]);
    } else {
      setGranularPerms([...(DEFAULT_ROLE_PERMISSIONS_MAP[u.role] || [])]);
    }
    setShowPermissions(u);
  };

  const toggleGranularPerm = (key) => {
    setGranularPerms(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const togglePermission = (key) => {
    setPermissionsForm(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const handleSavePermissions = async () => {
    setSavingPerms(true);
    try {
      const perms = useCustomPerms ? permissionsForm : null;
      const granular = useCustomPerms ? granularPerms : null;
      await axios.put(`${API_URL}/api/user-management/${showPermissions.id}`, {
        permissions: perms,
        granular_permissions: granular
      }, { withCredentials: true });
      fetchUsers();
      setShowPermissions(null);
    } catch (error) {
      alert('Error al guardar permisos');
    } finally {
      setSavingPerms(false);
    }
  };

  const activeUsers = users.filter(u => u.is_active !== false);
  const inactiveUsers = users.filter(u => u.is_active === false);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usuarios</h1>
          <p className="text-slate-500">Gestiona usuarios, roles y permisos del sistema</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingUser(null); setForm({ email: '', password: '', name: '', role: 'sales', phone: '' }); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          data-testid="add-user-button"
        >
          <UserPlus size={20} weight="bold" />
          Nuevo Usuario
        </button>
      </div>

      {/* Role summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {Object.entries(ROLE_CONFIG).map(([role, config]) => {
          const count = activeUsers.filter(u => u.role === role).length;
          const Icon = config.icon;
          return (
            <div key={role} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} className="text-slate-400" />
                <p className="text-xs text-slate-500">{config.label}</p>
              </div>
              <p className="text-2xl font-bold text-slate-900">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="user-form">
          <h3 className="font-semibold text-lg mb-4">{editingUser ? 'Editar Usuario' : 'Crear Usuario'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {!editingUser && (
              <div>
                <label className="form-label">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                  className="form-input" required data-testid="user-email-input" />
              </div>
            )}
            {!editingUser && (
              <div>
                <label className="form-label">Contrasena *</label>
                <input type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
                  className="form-input" required minLength={6} data-testid="user-password-input" />
              </div>
            )}
            <div>
              <label className="form-label">Nombre *</label>
              <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="form-input" required data-testid="user-name-input" />
            </div>
            <div>
              <label className="form-label">Rol *</label>
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})}
                className="form-input" data-testid="user-role-select">
                <option value="admin">Administrador</option>
                <option value="manager">Gerente</option>
                <option value="sales">Vendedor</option>
                <option value="engineer">Ingeniero</option>
                <option value="technician">Tecnico</option>
              </select>
            </div>
            <div>
              <label className="form-label">Telefono</label>
              <input type="text" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                className="form-input" data-testid="user-phone-input" />
            </div>
            <div className="md:col-span-3 flex justify-end gap-3">
              <button type="button" onClick={() => { setShowForm(false); setEditingUser(null); }}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
              <button type="submit" disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="save-user-button">
                {saving && <CircleNotch size={18} className="animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Permissions Modal - Granular View/Create/Edit/Delete */}
      {showPermissions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="permissions-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div>
                <h3 className="font-bold text-lg text-slate-900">Permisos Granulares</h3>
                <p className="text-sm text-slate-500">{showPermissions.name} ({ROLE_CONFIG[showPermissions.role]?.label})</p>
              </div>
              <button onClick={() => setShowPermissions(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Toggle custom vs role-based */}
              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                <div>
                  <p className="font-medium text-slate-900 text-sm">Permisos Personalizados</p>
                  <p className="text-xs text-slate-500">
                    {useCustomPerms ? 'Checkboxes granulares por modulo' : 'Usando permisos por defecto del rol'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!useCustomPerms) {
                      setPermissionsForm([...(ROLE_DEFAULTS[showPermissions.role] || [])]);
                      // Also build granular from defaults
                      const role = showPermissions.role;
                      const defaults = DEFAULT_ROLE_PERMISSIONS_MAP[role] || [];
                      setGranularPerms([...defaults]);
                    }
                    setUseCustomPerms(!useCustomPerms);
                  }}
                  className={`relative w-12 h-6 rounded-full transition-colors ${useCustomPerms ? 'bg-blue-600' : 'bg-slate-300'}`}
                  data-testid="custom-perms-toggle"
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${useCustomPerms ? 'left-[26px]' : 'left-0.5'}`} />
                </button>
              </div>

              {useCustomPerms && (
                <>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-slate-500 font-medium">{granularPerms.length} permisos activos</p>
                    <div className="flex gap-2">
                      <button onClick={() => {
                        const all = [];
                        PERMISSION_MODULES.forEach(mod => mod.actions.forEach(act => all.push(`${mod.key}_${act}`)));
                        setGranularPerms(all);
                      }} className="text-xs text-blue-600 hover:underline" data-testid="select-all-perms">Todos</button>
                      <button onClick={() => setGranularPerms([])}
                        className="text-xs text-red-500 hover:underline" data-testid="clear-all-perms">Ninguno</button>
                    </div>
                  </div>

                  {/* Granular permission grid */}
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Modulo</th>
                          {Object.entries(ACTION_LABELS).map(([key, label]) => (
                            <th key={key} className="px-2 py-3 text-center font-medium text-slate-600 text-xs">{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {PERMISSION_MODULES.map(mod => (
                          <tr key={mod.key} className="hover:bg-slate-50">
                            <td className="px-4 py-2.5 font-medium text-slate-800">{mod.label}</td>
                            {Object.keys(ACTION_LABELS).map(action => {
                              const permKey = `${mod.key}_${action}`;
                              const available = mod.actions.includes(action);
                              const active = granularPerms.includes(permKey);
                              return (
                                <td key={action} className="px-2 py-2.5 text-center">
                                  {available ? (
                                    <button
                                      onClick={() => toggleGranularPerm(permKey)}
                                      className={`w-6 h-6 rounded border-2 inline-flex items-center justify-center transition-all ${
                                        active
                                          ? 'bg-blue-600 border-blue-600 text-white'
                                          : 'border-slate-300 hover:border-blue-400'
                                      }`}
                                      data-testid={`perm-${permKey}`}
                                    >
                                      {active && <Check size={14} weight="bold" />}
                                    </button>
                                  ) : (
                                    <span className="text-slate-200">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Sidebar nav module access */}
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Acceso a Navegacion (Sidebar)</p>
                    <div className="grid grid-cols-3 gap-2">
                      {ALL_MODULES.map(mod => {
                        const isActive = permissionsForm.includes(mod.key);
                        return (
                          <button
                            key={mod.key}
                            onClick={() => togglePermission(mod.key)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                              isActive
                                ? 'bg-blue-50 border-blue-200 text-blue-700'
                                : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                            }`}
                            data-testid={`nav-perm-${mod.key}`}
                          >
                            {isActive ? <Check size={12} weight="bold" className="text-blue-600" /> : <div className="w-3 h-3 rounded border border-slate-300" />}
                            {mod.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {!useCustomPerms && (
                <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
                  <p className="font-medium mb-2">Permisos por defecto del rol {ROLE_CONFIG[showPermissions.role]?.label}:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(ROLE_DEFAULTS[showPermissions.role] || []).map(key => {
                      const mod = ALL_MODULES.find(m => m.key === key);
                      return mod ? (
                        <span key={key} className="px-2 py-0.5 bg-blue-100 rounded text-xs">{mod.label}</span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowPermissions(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm">
                Cancelar
              </button>
              <button
                onClick={handleSavePermissions}
                disabled={savingPerms}
                className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                data-testid="save-permissions-btn"
              >
                {savingPerms ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} weight="bold" />}
                {savingPerms ? 'Guardando...' : 'Guardar Permisos'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch size={32} className="animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Rol</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Permisos</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Estado</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeUsers.map((u) => (
                  <tr key={u.id} className="table-row-hover" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 font-medium text-slate-900">{u.name}</td>
                    <td className="px-6 py-4 text-slate-600">{u.email}</td>
                    <td className="px-6 py-4"><RoleBadge role={u.role} /></td>
                    <td className="px-6 py-4">
                      {u.permissions && u.permissions.length > 0 ? (
                        <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
                          Personalizado ({u.permissions.length})
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Por defecto</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Activo</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(u)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-600" title="Editar"
                          data-testid={`edit-user-${u.id}`}>
                          <PencilSimple size={18} />
                        </button>
                        <button onClick={() => openPermissions(u)} className="p-2 hover:bg-violet-50 rounded-lg text-violet-600" title="Permisos"
                          data-testid={`permissions-user-${u.id}`}>
                          <SlidersHorizontal size={18} />
                        </button>
                        <button onClick={() => { setShowResetPassword(u.id); setResetPw(''); }} className="p-2 hover:bg-slate-100 rounded-lg text-slate-600" title="Resetear contrasena"
                          data-testid={`reset-pw-${u.id}`}>
                          <Key size={18} />
                        </button>
                        {u.role !== 'admin' && (
                          <button onClick={() => handleDeactivate(u.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Desactivar"
                            data-testid={`deactivate-user-${u.id}`}>
                            <Prohibit size={18} />
                          </button>
                        )}
                      </div>
                      {showResetPassword === u.id && (
                        <div className="mt-2 flex items-center gap-2 justify-end">
                          <input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-40"
                            placeholder="Nueva contrasena" data-testid={`new-pw-input-${u.id}`} />
                          <button onClick={() => handleResetPassword(u.id)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                            data-testid={`confirm-reset-pw-${u.id}`}>OK</button>
                          <button onClick={() => setShowResetPassword(null)}
                            className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-sm">X</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {inactiveUsers.map((u) => (
                  <tr key={u.id} className="opacity-50" data-testid={`user-row-inactive-${u.id}`}>
                    <td className="px-6 py-4 font-medium text-slate-500">{u.name}</td>
                    <td className="px-6 py-4 text-slate-400">{u.email}</td>
                    <td className="px-6 py-4"><RoleBadge role={u.role} /></td>
                    <td className="px-6 py-4"><span className="text-xs text-slate-400">-</span></td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium">Inactivo</span>
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
